import type { MonoBitmap } from "@luckymenu/print-core";

/**
 * A 1-bit bitmap as a string for `KeyValueStorage` (the dithered logo cache,
 * P5-T27). Base64 written out, so it needs no `btoa` or `Buffer`.
 */

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

export function toBase64(bytes: Uint8Array): string {
	let out = "";
	for (let i = 0; i < bytes.length; i += 3) {
		const a = bytes[i] ?? 0;
		const b = bytes[i + 1] ?? 0;
		const c = bytes[i + 2] ?? 0;
		const n = (a << 16) | (b << 8) | c;
		out += ALPHABET[(n >> 18) & 63];
		out += ALPHABET[(n >> 12) & 63];
		out += i + 1 < bytes.length ? ALPHABET[(n >> 6) & 63] : "=";
		out += i + 2 < bytes.length ? ALPHABET[n & 63] : "=";
	}
	return out;
}

export function fromBase64(text: string): Uint8Array {
	const clean = text.replace(/[^A-Za-z0-9+/]/g, "");
	const out = new Uint8Array(Math.floor((clean.length * 3) / 4));
	let n = 0;
	let bits = 0;
	let j = 0;
	for (const ch of clean) {
		n = (n << 6) | ALPHABET.indexOf(ch);
		bits += 6;
		if (bits >= 8) {
			bits -= 8;
			out[j++] = (n >> bits) & 0xff;
		}
	}
	return out.subarray(0, j);
}

export function encodeBitmap(bitmap: MonoBitmap): string {
	return JSON.stringify({ w: bitmap.width, h: bitmap.height, d: toBase64(bitmap.data) });
}

export function decodeBitmap(text: string): MonoBitmap | null {
	try {
		const raw = JSON.parse(text) as { w?: unknown; h?: unknown; d?: unknown };
		if (typeof raw.w !== "number" || typeof raw.h !== "number" || typeof raw.d !== "string") {
			return null;
		}
		const data = fromBase64(raw.d);
		if (data.length !== Math.ceil(raw.w / 8) * raw.h) return null;
		return { width: raw.w, height: raw.h, data };
	} catch {
		return null;
	}
}
